import { useMemo, useState } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IDropDownExtendedItem } from "app/pages/sales-intelligence/common-components/dropdown/ControlledMultiDropdown";
import type { TabbedSelectComboboxTab } from "app/pages/sales-intelligence/common-components/dropdown/TabbedSelectCombobox";
import { TabbedSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/TabbedSelectCombobox";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { IFilterViewBaseModelProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import IosCategoriesDropdown from "app/pages/sales-intelligence/modules/apps-search-filters/components/dropdown/IosCategoriesDropdown/IosCategoriesDropdown";
import useDropdownWithAllSelectedItems from "app/pages/sales-intelligence/modules/apps-search-filters/hooks/useDropdownWithAllSelectedItems";
import useManageAllCategories from "app/pages/sales-intelligence/modules/apps-search-filters/hooks/useManageAllCategories";
import {
    StyledDropdownContainer,
    StyledDropdownLabel,
    StyledRadioButton,
    StyledRadioButtonContainer,
} from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppIosCategoryFilter/styles";
import { SELECTED_ALL_PREFIX } from "app/pages/sales-intelligence/modules/find-apps-filters/constants";
import { AppsCategoryTypes } from "app/pages/sales-intelligence/modules/find-apps-filters/types/commonEnums";
import type {
    AppsCategoryItemDto,
    AppsIosCategoryDescriptionDto,
} from "app/pages/sales-intelligence/modules/find-apps-filters/types/description.dto";
import useDropdownTabbedSearch from "app/pages/sales-intelligence/modules/technology/hook/useDropdownTabbedSearch";
import type { IAppsIosCategoryFilter } from "app/pages/sales-intelligence/store/find-apps/types";

const AppIosCategoryFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseModelProps<IAppsIosCategoryFilter, AppsIosCategoryDescriptionDto>) => {
    const [typeCategory, setTypeCategory] = useState<string>(AppsCategoryTypes.All);

    const { selectedAllCategory, setSelectedAllCategory, initSelectedAllCategory } = useManageAllCategories<
        IAppsIosCategoryFilter | undefined,
        AppsIosCategoryDescriptionDto
    >({
        options: filter.options,
        values: value,
    });

    const handleTypeChange = (value: string) => {
        setTypeCategory(value);
        initSelectedAllCategory();
        onFilterUpdate({
            values: [],
            type: value,
        });
    };

    const tabs = useMemo(() => {
        const categoryValues = value?.values || [];
        const isAllCategory = typeCategory === AppsCategoryTypes.All;

        if (isAllCategory) {
            const mappedData = filter.options.categories.map((option: AppsCategoryItemDto, i: number) => {
                return {
                    id: i.toString(),
                    name: option.name,
                    items: option.subCategories.map((item: string) => ({
                        name: item,
                        isSelected: categoryValues.includes(item),
                    })),
                };
            });

            return mappedData;
        }

        return [
            {
                id: "primary-category-filter",
                name: "Primary category filter",
                items: filter.options.primaryCategories.map((item: string) => ({
                    id: item,
                    name: item,
                    text: item,
                    isSelected: categoryValues.includes(item),
                })),
            },
        ];
    }, [filter.options, value, typeCategory]);

    const { tabsWithSortedItems, searchTerm, searchProps } = useDropdownTabbedSearch({
        tabs,
        searchPlaceholder: translate("si.find_apps.filters.ios-category.dropdown.placeholder"),
    });

    const { tabsWithAllSelectedItems } = useDropdownWithAllSelectedItems({
        tabs: tabsWithSortedItems,
        selectedAllCategory,
    });

    const handleOnItemClick = (item: { name: string }) => {
        const categoryValues = value?.values || [];
        const isAllCategory = selectedAllCategory.hasOwnProperty(item.name);

        if (isAllCategory) {
            const isSelected = !selectedAllCategory[item.name];

            setSelectedAllCategory({
                ...selectedAllCategory,
                [item.name]: isSelected,
            });

            const categoryItems = filter.options.categories.find((category) =>
                item.name.includes(category.name.trim().toLowerCase()),
            );

            let filterUpdatesParams: IAppsIosCategoryFilter | undefined = undefined;
            if (isSelected && categoryItems) {
                filterUpdatesParams = {
                    values: Array.from(new Set([...categoryValues, ...categoryItems.subCategories])),
                    type: typeCategory,
                };
            } else {
                const filteredCategoryValues: string[] = categoryValues.filter(
                    (item: string) => !categoryItems?.subCategories.includes(item),
                );
                if (filteredCategoryValues.length > 0) {
                    filterUpdatesParams = {
                        values: filteredCategoryValues,
                        type: typeCategory,
                    };
                }
            }

            onFilterUpdate(filterUpdatesParams);
        } else {
            const category = filter.options.categories.find((categoryItem) =>
                categoryItem.subCategories.includes(item.name),
            );

            if (category) {
                const selectedAllCategoryName = `${SELECTED_ALL_PREFIX} ${category.name.trim().toLowerCase()}`;

                if (categoryValues.includes(item.name)) {
                    setSelectedAllCategory({
                        ...selectedAllCategory,
                        [selectedAllCategoryName]: false,
                    });
                } else {
                    const isFullCategory = category.subCategories.every((categoryItem: string) =>
                        [...categoryValues, item.name].includes(categoryItem),
                    );

                    if (isFullCategory) {
                        setSelectedAllCategory({
                            ...selectedAllCategory,
                            [selectedAllCategoryName]: true,
                        });
                    }
                }
            }

            let filterUpdatesParams: IAppsIosCategoryFilter | undefined = undefined;
            if (!categoryValues.includes(item.name)) {
                filterUpdatesParams = {
                    values: [...categoryValues, item.name],
                    type: typeCategory,
                };
            } else {
                const filteredCategoryValues: string[] = categoryValues.filter(
                    (category: string) => category !== item.name,
                );

                if (filteredCategoryValues.length > 0) {
                    filterUpdatesParams = {
                        values: filteredCategoryValues,
                        type: typeCategory,
                    };
                }
            }

            onFilterUpdate(filterUpdatesParams);
        }
    };

    const handlePrimaryOnItemClick = (item: IDropDownExtendedItem) => {
        if (item.text) {
            const categoryValues = value?.values || [];

            let filterUpdatesParams: IAppsIosCategoryFilter | undefined = undefined;
            if (!categoryValues.includes(item.text)) {
                filterUpdatesParams = {
                    values: [...categoryValues, item.text],
                    type: typeCategory,
                };
            } else {
                const filteredCategoryValues: string[] = categoryValues.filter(
                    (category: string) => category !== item.text,
                );

                if (filteredCategoryValues.length > 0) {
                    filterUpdatesParams = {
                        values: filteredCategoryValues,
                        type: typeCategory,
                    };
                }
            }

            onFilterUpdate(filterUpdatesParams);
        }
    };

    const dropdownTitle = translate("si.find_apps.filters.ios-category.dropdown.title");

    return (
        <div>
            <StyledRadioButtonContainer>
                <StyledRadioButton
                    itemLabel={translate("si.find_apps.filters.ios-category.all-category.label")}
                    itemSelected={typeCategory === AppsCategoryTypes.All}
                    onClick={() => handleTypeChange(AppsCategoryTypes.All)}
                    buttonSize="sm"
                />

                <StyledRadioButton
                    itemLabel={translate("si.find_apps.filters.ios-category.primary-category.label")}
                    itemSelected={typeCategory == AppsCategoryTypes.Primary}
                    onClick={() => handleTypeChange(AppsCategoryTypes.Primary)}
                    buttonSize="sm"
                />
            </StyledRadioButtonContainer>

            <StyledDropdownContainer>
                <I18n
                    data-automation="ios-category-title-text"
                    translatable={{ key: "si.find_apps.filters.ios-category.label" }}
                    element={StyledDropdownLabel}
                />
                {typeCategory === AppsCategoryTypes.All ? (
                    <TabbedSelectCombobox
                        tabs={tabsWithAllSelectedItems as readonly TabbedSelectComboboxTab[]}
                        onItemClick={handleOnItemClick}
                        searchTerm={searchTerm}
                        onSearchChange={searchProps.onChange}
                        searchPlaceholder={searchProps.placeholder}
                        buttonText={dropdownTitle}
                        dataAutomation="app-ios-category-filter"
                    />
                ) : (
                    <IosCategoriesDropdown
                        items={tabsWithSortedItems[0].items as IDropDownExtendedItem[]}
                        selectedItems={value?.values || []}
                        onSelect={handlePrimaryOnItemClick}
                        buttonText={translate("si.find_apps.filters.ios-category.dropdown.button")}
                        placeholder={translate("si.find_apps.filters.ios-category.dropdown.placeholder")}
                        searchTerm={searchTerm}
                        onSearchChange={searchProps.onChange}
                    />
                )}
            </StyledDropdownContainer>
        </div>
    );
};

export default AppIosCategoryFilter;
